<template>
  <AppShell>
    <section class="space-y-8">
      <div class="space-y-3">
        <p class="app-eyebrow">{{ t("kyc.eyebrow") }}</p>
        <h1 class="text-3xl font-bold tracking-tight">{{ headline }}</h1>
        <p class="max-w-2xl text-sm leading-7 text-brand-muted-strong">{{ description }}</p>
      </div>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardContent class="space-y-6 p-8">
          <div class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div class="flex items-start gap-4">
              <span
                class="flex size-12 shrink-0 items-center justify-center rounded-full"
                :class="iconWrapperClass"
              >
                <component :is="statusIcon" class="size-6" />
              </span>
              <div class="space-y-1">
                <p class="app-eyebrow">{{ t("kyc.status") }}</p>
                <p class="text-xl font-bold text-brand-ink">{{ statusLabel }}</p>
                <p class="text-sm text-brand-muted">{{ customerName }}</p>
              </div>
            </div>

            <Button
              v-if="isRejected"
              class="h-12 rounded-none border-0 bg-brand-fill px-8 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover"
              @click="restartKyc"
            >
              <RefreshCw class="size-4" />
              {{ t("kyc.restart") }}
            </Button>

            <Button
              v-if="isApproved"
              class="h-12 rounded-none border-0 bg-brand-fill px-8 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover"
              @click="goToPayment"
            >
              {{ t("kyc.toPayment") }}
            </Button>

            <Button
              v-if="isPending"
              class="hidden! h-12 rounded-none border-0 bg-brand-fill px-8 text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover"
              :disabled="kycStarting"
              @click="startKyc"
            >
              {{ t("kyc.start") }}
            </Button>
          </div>

          <!-- TODO: info an den User was fuer ihn zu tun ist (ev. Support kontaktieren) -->
          <div v-if="mismatchEntries.length" class="border-t border-brand-line pt-6">
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <p class="app-eyebrow">{{ t("kyc.mismatchesTitle") }}</p>
              <Button
                v-if="hasMissingMismatchValue"
                variant="outline"
                class="h-10 rounded-none px-4 text-[11px] font-bold uppercase tracking-[0.14em]"
                @click="goToDocumentUpload"
              >
                <Upload class="size-4" />
                {{ t("kyc.uploadDocument") }}
              </Button>
            </div>
            <div class="mt-4 grid gap-3">
              <div
                v-for="mismatch in mismatchEntries"
                :key="mismatch.field"
                class="grid gap-2 border border-brand-line p-4 text-sm md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
              >
                <div>
                  <p class="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-muted">
                    {{ t("kyc.mismatchField") }}
                  </p>
                  <p class="break-words font-semibold text-brand-ink">{{ mismatch.field }}</p>
                </div>
                <div>
                  <p class="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-muted">
                    {{ t("kyc.mismatchActual") }}
                  </p>
                  <p class="break-words text-brand-muted-strong">{{ mismatch.actual }}</p>
                </div>
                <div>
                  <p class="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-muted">
                    {{ t("kyc.mismatchExpected") }}
                  </p>
                  <p class="break-words text-brand-muted-strong">{{ mismatch.expected }}</p>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isMockProvider" class="flex flex-wrap gap-3 border-t border-brand-line pt-6">
            <Button
              v-for="debugStatus in debugStatuses"
              :key="debugStatus.status"
              variant="outline"
              class="h-10 rounded-none px-4 text-[11px] font-bold uppercase tracking-[0.14em]"
              :disabled="debugSaving || !providerSessionId"
              @click="setDebugKycStatus(debugStatus.status)"
            >
              {{ debugStatus.label }}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import {
  createOrvalRequestOptions,
  CurrentKycDtoStatus,
  KycDtoStatus,
  handleKycCallback,
} from "@token/shared";
import type { KycDtoStatus as KycDtoStatusValue } from "@token/shared";
import { CheckCircle2, Clock, RefreshCw, ShieldAlert, Upload } from "lucide-vue-next";
import { toast } from "vue-sonner";
import AppShell from "@/components/AppShell.vue";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { createOnboardingApi } from "@/features/onboarding";
import { KycStartRequestDtoKycMethod } from "@token/shared";
import { useApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useConfig } from "@/lib/config";
import { useCustomerSession } from "@/features/customer-session";
import { useI18n } from "@/lib/i18n";

const auth = useAuth();
const api = useApi();
const config = useConfig();
const router = useRouter();
const isMockProvider = computed(() => config.kyc?.provider?.toUpperCase() === "MOCK");
const customerSession = useCustomerSession();
const onboardingApi = createOnboardingApi(api);
const { t } = useI18n();
const debugSaving = ref(false);
const kycStarting = ref(false);
let pollingInterval: number | null = null;

const customer = computed(() => customerSession.current);
const kycRecord = computed(() => customer.value?.kyc?.kyc ?? null);
const providerSessionId = computed(() => kycRecord.value?.providerSessionId ?? null);
const mismatchEntries = computed(() => formatMismatches(kycRecord.value?.providerMismatches));
const hasMissingMismatchValue = computed(() => hasExactMissingMismatchValue(kycRecord.value?.providerMismatches));
const status = computed(
  () =>
    kycRecord.value?.status ??
    customer.value?.kyc?.status ??
    CurrentKycDtoStatus.NOT_STARTED,
);
const debugStatuses: Array<{ label: string; status: KycDtoStatusValue }> = [
  { label: "Pending", status: KycDtoStatus.PENDING },
  { label: "Approved", status: KycDtoStatus.APPROVED },
  { label: "Rejected", status: KycDtoStatus.REJECTED },
];
const isNotStarted = computed(() => status.value === CurrentKycDtoStatus.NOT_STARTED);
const isApproved = computed(() => status.value === CurrentKycDtoStatus.APPROVED);
const isRejected = computed(() => status.value === CurrentKycDtoStatus.REJECTED);
const isPending = computed(() => status.value === CurrentKycDtoStatus.PENDING);

onMounted(() => {
  pollKycStatus();
  pollingInterval = window.setInterval(pollKycStatus, 10_000);
});

onUnmounted(() => {
  if (pollingInterval) window.clearInterval(pollingInterval);
});

const customerName = computed(() => {
  if (customer.value) return [customer.value.firstName, customer.value.lastName].filter(Boolean).join(" ") || "Portal User";
  return auth.state.user?.name ?? "Portal User";
});

const statusLabel = computed(() => {
  if (isNotStarted.value) return t("kyc.notStarted");
  if (isApproved.value) return t("kyc.approved");
  if (isRejected.value) return t("kyc.rejected");
  return t("kyc.pending");
});

const headline = computed(() => {
  if (isNotStarted.value) return t("kyc.notStartedHeadline");
  if (isApproved.value) return t("kyc.approvedHeadline");
  if (isRejected.value) return t("kyc.rejectedHeadline");
  return t("kyc.pendingHeadline");
});

const description = computed(() => {
  if (isNotStarted.value) return t("kyc.notStartedDescription");
  if (isApproved.value) return t("kyc.approvedDescription");
  if (isRejected.value) return t("kyc.rejectedDescription");
  return t("kyc.pendingDescription");
});

const statusIcon = computed(() => {
  if (isApproved.value) return CheckCircle2;
  if (isRejected.value) return ShieldAlert;
  return Clock;
});

const iconWrapperClass = computed(() => {
  if (isApproved.value) return "bg-emerald-50 text-emerald-700";
  if (isRejected.value) return "bg-destructive/10 text-destructive";
  return "bg-brand-fill/10 text-brand-ink";
});

function restartKyc() {
  router.push({ name: "onboarding", query: { restart: "1" } });
}

function goToPayment() {
  router.push({ name: "token-purchase" });
}

function goToDocumentUpload() {
  router.push({ name: "kyc-document-upload", query: { kycId: kycRecord.value?.id } });
}

async function startKyc() {
  kycStarting.value = true;
  try {
    const verification = await onboardingApi.startKyc(
      config.kyc?.methods?.[0]?.method ?? KycStartRequestDtoKycMethod.WEBIDPAY,
    );
    await customerSession.sync({ force: true });
    if (verification.redirectUrl) {
      window.open(verification.redirectUrl, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t("onboarding.saveError"));
  } finally {
    kycStarting.value = false;
  }
}

function pollKycStatus() {
  customerSession.sync({ force: true }).catch(() => undefined);
}

function formatMismatches(value: unknown): Array<{ field: string; expected: string; actual: string }> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];

  const mismatches = (value as Record<string, unknown>).Mismatches;
  if (!Array.isArray(mismatches)) return [];

  return mismatches.flatMap((mismatch) => {
    if (!mismatch || typeof mismatch !== "object" || Array.isArray(mismatch)) return [];

    const details = mismatch as Record<string, unknown>;
    return {
      field: formatMismatchValue(details.FieldName),
      expected: formatMismatchValue(details.KycValue),
      actual: formatMismatchValue(details.Value),
    };
  });
}

function hasExactMissingMismatchValue(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const mismatches = (value as Record<string, unknown>).Mismatches;
  if (!Array.isArray(mismatches)) return false;

  return mismatches.some((mismatch) => {
    if (!mismatch || typeof mismatch !== "object" || Array.isArray(mismatch)) return false;
    return (mismatch as Record<string, unknown>).KycValue === "--";
  });
}

function formatMismatchValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

async function setDebugKycStatus(nextStatus: KycDtoStatusValue) {
  if (!providerSessionId.value) return;

  debugSaving.value = true;
  try {
    await handleKycCallback(
      "mock",
      createOrvalRequestOptions(api, {
        body: JSON.stringify({ providerSessionId: providerSessionId.value, status: nextStatus }),
        headers: { "Content-Type": "application/json" },
      }),
    );
    window.location.reload();
  } catch (error) {
    debugSaving.value = false;
    toast.error(error instanceof Error ? error.message : t("onboarding.saveError"));
  }
}
</script>
